import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Search,
  Boxes,
  Sparkles,
  CheckCircle2,
  XCircle,
  type LucideProps,
} from 'lucide-react';
import {
  FOREST_GREEN,
  CHAMPAGNE,
  MID_GRAY,
  LIGHT_GRAY,
  CHARCOAL,
  ERROR,
  SHADOWS,
  WHITE,
} from '@/styles/tokens';

export type AIGenerationStage =
  | 'idle'
  | 'selecting'
  | 'generating'
  | 'finalizing'
  | 'complete'
  | 'error'
  | null;

interface AIGenerationProgressModalProps {
  isOpen: boolean;
  currentStage: AIGenerationStage;
  error?: string | null;
  onClose?: () => void;
}

interface StepConfig {
  id: 'selecting' | 'generating' | 'finalizing';
  icon: React.FC<LucideProps>;
  label: string;
  description: string;
}

const GENERATION_STEPS: StepConfig[] = [
  {
    id: 'selecting',
    icon: Search,
    label: 'Template Selection',
    description: 'Finding best templates...',
  },
  {
    id: 'generating',
    icon: Boxes,
    label: 'Component Generation',
    description: 'Creating components...',
  },
  {
    id: 'finalizing',
    icon: Sparkles,
    label: 'Finalizing',
    description: 'Applying finishing touches...',
  },
];

export const AIGenerationProgressModal: React.FC<AIGenerationProgressModalProps> = ({
  isOpen,
  currentStage,
  error,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      setTimeout(() => setShouldRender(false), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (currentStage === 'complete' && onClose) {
      const timer = setTimeout(onClose, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [currentStage, onClose]);

  if (!shouldRender) return null;

  // Inject slow pulse keyframes
  const pulseKeyframes = `
    @keyframes slowPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.7; transform: scale(0.95); }
    }
  `;

  const getCurrentStepIndex = (): number => {
    if (currentStage === 'complete') return GENERATION_STEPS.length;
    if (currentStage === 'error' || currentStage === 'idle' || currentStage === null) return -1;
    return GENERATION_STEPS.findIndex((s) => s.id === currentStage);
  };

  const currentStepIndex = getCurrentStepIndex();
  const currentStep = currentStepIndex >= 0 && currentStepIndex < GENERATION_STEPS.length
    ? GENERATION_STEPS[currentStepIndex]
    : null;

  const getStepStatus = (index: number): 'pending' | 'active' | 'complete' | 'error' => {
    if (currentStage === 'error') {
      if (index === currentStepIndex) return 'error';
      if (index < currentStepIndex) return 'complete';
      return 'pending';
    }
    if (currentStage === 'complete') return 'complete';
    if (index < currentStepIndex) return 'complete';
    if (index === currentStepIndex) return 'active';
    return 'pending';
  };

  const progressPercentage = currentStage === 'complete'
    ? 100
    : currentStepIndex >= 0
      ? (currentStepIndex / (GENERATION_STEPS.length - 1)) * 100
      : 0;

  return (
    <>
      <style>{pulseKeyframes}</style>
      <div
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center transition-all duration-300',
          isVisible ? 'opacity-100' : 'opacity-0'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="generation-progress-title"
      >
        {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={currentStage === 'complete' || currentStage === 'error' ? onClose : undefined}
      />

      {/* Modal */}
      <div
        className={cn(
          'relative bg-white rounded-xl p-8 max-w-2xl w-full mx-4 transition-all duration-300',
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        )}
        style={{ boxShadow: SHADOWS.modal }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h2
            id="generation-progress-title"
            className="text-xl font-semibold mb-2"
            style={{ color: CHARCOAL.light }}
          >
            {currentStage === 'complete'
              ? 'Generation Complete!'
              : currentStage === 'error'
                ? 'Generation Failed'
                : 'Generating Your Page'}
          </h2>
          {currentStep && currentStage !== 'complete' && currentStage !== 'error' && (
            <p className="text-sm" style={{ color: MID_GRAY.DEFAULT }}>
              {currentStep.description}
            </p>
          )}
          {currentStage === 'complete' && (
            <p className="text-sm" style={{ color: FOREST_GREEN.DEFAULT }}>
              Your page has been created successfully
            </p>
          )}
        </div>

        {/* Progress Indicators */}
        <div
          className="relative px-4"
          role="progressbar"
          aria-valuenow={currentStepIndex + 1}
          aria-valuemin={0}
          aria-valuemax={GENERATION_STEPS.length}
          aria-label={`Generation progress: step ${currentStepIndex + 1} of ${GENERATION_STEPS.length}`}
        >
          {/* Progress Line Background */}
          <div
            className="absolute top-6 left-[calc(12.5%+8px)] right-[calc(12.5%+8px)] h-1 rounded-full"
            style={{ backgroundColor: MID_GRAY.lighter }}
          />

          {/* Active Progress Line */}
          <div
            className="absolute top-6 left-[calc(12.5%+8px)] h-1 rounded-full transition-all duration-500 ease-out"
            style={{
              backgroundColor: currentStage === 'error' ? ERROR.DEFAULT : FOREST_GREEN.DEFAULT,
              width: `calc(${progressPercentage}% * 0.75)`,
            }}
          />

          {/* Step Items */}
          <div className="relative flex justify-between">
            {GENERATION_STEPS.map((step, index) => {
              const Icon = step.icon;
              const status = getStepStatus(index);

              return (
                <div key={step.id} className="flex flex-col items-center" style={{ width: '25%' }}>
                  {/* Icon Container */}
                  <div className="relative">
                    <div
                      className={cn(
                        'w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300'
                      )}
                      style={{
                        backgroundColor:
                          status === 'complete'
                            ? FOREST_GREEN.DEFAULT
                            : status === 'active'
                              ? CHAMPAGNE.darker
                              : status === 'error'
                                ? ERROR.DEFAULT
                                : LIGHT_GRAY.DEFAULT,
                        animation: status === 'active' ? 'slowPulse 2.5s ease-in-out infinite' : undefined,
                      }}
                    >
                      {status === 'active' ? (
                        <Icon className="w-5 h-5" color={CHARCOAL.DEFAULT} />
                      ) : status === 'complete' ? (
                        <CheckCircle2 className="w-5 h-5" color={WHITE} />
                      ) : status === 'error' ? (
                        <XCircle className="w-5 h-5" color={WHITE} />
                      ) : (
                        <Icon
                          className="w-5 h-5"
                          color={MID_GRAY.DEFAULT}
                        />
                      )}
                    </div>
                  </div>

                  {/* Label */}
                  <span
                    className="mt-3 text-xs font-medium text-center leading-tight"
                    style={{
                      color:
                        status === 'complete'
                          ? FOREST_GREEN.DEFAULT
                          : status === 'active'
                            ? CHARCOAL.DEFAULT
                            : status === 'error'
                              ? ERROR.DEFAULT
                              : MID_GRAY.light,
                    }}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Screen Reader Announcements */}
        <div className="sr-only" aria-live="polite">
          {currentStage === 'selecting' && 'Step 1 of 3: Selecting templates'}
          {currentStage === 'generating' && 'Step 2 of 3: Generating components'}
          {currentStage === 'finalizing' && 'Step 3 of 3: Finalizing'}
          {currentStage === 'complete' && 'Generation complete'}
          {currentStage === 'error' && 'Generation failed'}
        </div>

        {/* Error Message */}
        {currentStage === 'error' && error && (
          <div
            className="mt-6 p-4 rounded-lg"
            style={{ backgroundColor: ERROR.bg }}
          >
            <p className="text-sm text-center" style={{ color: ERROR.text }}>
              {error}
            </p>
          </div>
        )}

        {/* Actions */}
        {(currentStage === 'error' || currentStage === 'complete') && onClose && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 text-sm font-medium rounded-lg transition-colors"
              style={{
                backgroundColor: currentStage === 'error' ? ERROR.DEFAULT : FOREST_GREEN.DEFAULT,
                color: WHITE,
              }}
            >
              {currentStage === 'error' ? 'Close' : 'Done'}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};
